import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { type Question, type Quiz as BaseQuiz } from "@/lib/quiz-api";
import { Heart, Sparkles, Flower } from "lucide-react";
import { slideInFromBottom, pulseAnimation } from "@/lib/animations";
import { cn } from "@/lib/utils";
import { QuizTheme } from "@/lib/theme-presets";

// Extend the Quiz type to include theme
interface Quiz extends BaseQuiz {
  theme: QuizTheme;
}

interface QuizQuestionProps {
  question: Question;
  onAnswer: (answer: string) => void;
  currentNumber: number;
  total: number;
  quiz: Quiz;
}

export default function QuizQuestion({
  question,
  onAnswer,
  currentNumber,
  total,
  quiz,
}: QuizQuestionProps) {
  const options = [
    { label: question.optionA, value: "option-a" },
    { label: question.optionB, value: "option-b" },
  ];

  // Get theme-appropriate icon
  const QuestionIcon = () => {
    const iconClass = "h-8 w-8 mb-2";
    const iconStyle = { color: quiz.theme.primaryColor };
    
    switch(quiz.theme.welcomeIcon) {
      case 'hearts': 
        return <Heart className={iconClass} style={iconStyle} />;
      case 'rings': 
        return <Sparkles className={iconClass} style={iconStyle} />;
      case 'doves': 
        return <Flower className={iconClass} style={iconStyle} />;
      default: 
        return <Heart className={iconClass} style={iconStyle} />;
    }
  };

  // Calculate progress percentage
  const progressPercentage = ((currentNumber - 1) / total) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="relative z-10 flex flex-col items-center justify-center min-h-[60vh]"
    >
      {/* Decorative elements */}
      <div className="absolute -top-10 -left-10 opacity-10 pointer-events-none hidden md:block">
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" 
            fill={quiz.theme.primaryColor} />
        </svg>
      </div>
      <div className="absolute -bottom-10 -right-10 opacity-10 pointer-events-none hidden md:block">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" 
            fill={quiz.theme.primaryColor} />
        </svg>
      </div>
      
      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-100 rounded-full mb-8 overflow-hidden">
        <div 
          className="h-full transition-all duration-300 ease-in-out"
          style={{ 
            width: `${progressPercentage}%`,
            backgroundColor: quiz.theme.primaryColor
          }}
        />
      </div>
      
      <Card className="mb-8 border shadow-lg backdrop-blur-sm w-full mx-auto" 
        style={{ 
          borderColor: `${quiz.theme.primaryColor}40`,
          background: `linear-gradient(135deg, white, ${quiz.theme.secondaryColor}30)`
        }}>
        <CardContent className="p-8 md:p-12">
          <div className="text-center mb-8">
            <motion.div 
              className="flex justify-center items-center"
              initial="hidden"
              animate="visible"
              variants={slideInFromBottom}
            >
              {QuestionIcon()}
            </motion.div>
            <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full inline-block mb-4">
              Question {currentNumber} of {total}
            </span>
            <h2 className="text-xl sm:text-2xl font-medium mt-4 leading-relaxed"
              style={{ color: quiz.theme.primaryColor }}>
              {question.text}
            </h2>
          </div>

          <div className="grid gap-5">
            {options.map((option, index) => (
              <motion.div
                key={option.value}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.2 }}
                whileHover="hover"
                variants={{
                  hover: {
                    scale: 1.02,
                    transition: {
                      duration: 0.2,
                    },
                  },
                }}
              >
                <Button
                  variant="outline"
                  className={cn(
                    "h-auto py-5 px-5 text-left justify-start w-full relative overflow-hidden group",
                    "transition-all duration-300 ease-in-out",
                    "hover:shadow-md hover:border-opacity-100",
                    "active:scale-[0.98]"
                  )}
                  style={{
                    borderColor: `${quiz.theme.primaryColor}50`,
                    borderWidth: "2px",
                    color: quiz.theme.primaryColor,
                    borderRadius: quiz.theme.buttonStyle === 'pill' ? '9999px' : 
                              quiz.theme.buttonStyle === 'square' ? '0px' : '0.5rem'
                  }}
                  onClick={() => onAnswer(option.value)}
                >
                  {/* Option indicator with background */}
                  <span 
                    className={cn(
                      "flex items-center justify-center w-8 h-8 rounded-full mr-4 font-bold text-white shrink-0",
                      "transition-all duration-300"
                    )}
                    style={{ backgroundColor: quiz.theme.primaryColor }}
                  >
                    {option.value === "option-a" ? "A" : "B"}
                  </span>
                  
                  {/* Option text */}
                  <span className="font-medium text-base sm:text-lg">{option.label}</span>
                  
                  {/* Hover effect overlay */}
                  <div 
                    className="absolute inset-0 w-full h-full opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                    style={{ backgroundColor: quiz.theme.primaryColor }}
                  />
                </Button>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}